import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { QuotaPayloadService } from '../../../services/quotas/quota-payload.service';
import { catchError, filter, map, share, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import {
  UnicornPayload,
  UnicornQuotas,
} from '../../../../shared/interfaces/unicorn.interface';
import { BuyerSurveyResponse } from '../../../../shared/interfaces/survey.interface';
import { BuyerApiService } from '../../../../shared/services/buyer-api/survey.service';
import { NestedQuota } from '../../../../shared/interfaces/quota.interface';
import { SURVEY_STATUS } from '../../../../shared/constants/survey-status';
import { SurveyMetaDataService } from '../../../../shared/services/survey-meta-data/survey-meta-data.service';
import { DifficultQuota } from '../../../../shared/interfaces/feasibility.interface';
import { FeasibilityData } from '../../../../shared/interfaces/survey-meta-data.interface';
import { SurveyUnicorns } from '../../../domain/survey-unicorns';

@Component({
  selector: 'ps-ui-create-survey-info-header',
  templateUrl: './create-survey-info-header.component.html',
  styleUrls: ['./create-survey-info-header.component.css'],
})
export class CreateSurveyInfoHeaderComponent implements OnInit, OnDestroy {
  @Input() surveyDetails = <BuyerSurveyResponse>{};

  private _subscriptions = new Subscription();
  private _nestedQuotas: NestedQuota[] = [];

  public unicornQuotas: UnicornQuotas[] = [];
  public difficultQuotas: DifficultQuota[] = [];

  constructor(
    private _quotaPayloadService: QuotaPayloadService,
    private _buyerApiService: BuyerApiService,
    private _surveyMetaDataService: SurveyMetaDataService
  ) {}

  ngOnInit(): void {
    this._handleFetchingOfSurveyUnicorns();
    this._handleFetchingOfSurveyDifficultQuotas();
  }

  private _handleFetchingOfSurveyUnicorns(): void {
    const nestedQuotas$ = this._quotaPayloadService.getNestedQuotas().pipe(
      tap((nestedQuotas) => (this._nestedQuotas = nestedQuotas)),
      share()
    );

    this._subscriptions.add(
      nestedQuotas$
        .pipe(filter((nestedQuotas) => !nestedQuotas || !nestedQuotas.length))
        .subscribe(() => {
          this.unicornQuotas = [];
          this._quotaPayloadService.setSurveyUnicorns(this.unicornQuotas);
        })
    );

    this._subscriptions.add(
      nestedQuotas$
        .pipe(filter((nestedQuotas) => !!nestedQuotas?.length))
        .pipe(
          switchMap((nestedQuotas) => {
            const payload: UnicornPayload = {
              survey_localization: this.surveyDetails?.survey_localization,
              quotas: nestedQuotas as UnicornQuotas[],
            };
            return this._buyerApiService
              .findSurveyUnicorns(payload)
              .pipe(catchError((error) => of({ survey_unicorns: [] })));
          })
        )
        .subscribe((response) => {
          this.unicornQuotas = response.survey_unicorns;
          this._quotaPayloadService.setSurveyUnicorns(this.unicornQuotas);
        })
    );
  }

  private _handleFetchingOfSurveyDifficultQuotas(): void {
    this._subscriptions.add(
      this._surveyMetaDataService
        .getFeasibilityData()
        .pipe(
          filter((data) => !!data && !data.loading),
          filter(
            () => this.surveyDetails?.ps_survey_status === SURVEY_STATUS.DRAFT
          ),
          switchMap((data) => this._mapDifficultQuotas(data)),
          // any quota that is impossible needs to be filtered out from difficult quotas
          map((difficultQuotas) =>
            difficultQuotas.filter(
              (d) =>
                !new SurveyUnicorns(this.unicornQuotas).hasQuota(
                  d.buyer_quota_id
                )
            )
          )
        )
        .subscribe((difficultQuotas) => {
          this.difficultQuotas = difficultQuotas;
          this._quotaPayloadService.setSurveyDifficultQuotas(
            this.difficultQuotas
          );
        })
    );
  }

  private _mapDifficultQuotas(
    feasibilityData: FeasibilityData
  ): Observable<DifficultQuota[]> {
    return this._quotaPayloadService.getQuotas().pipe(
      map((quotas) => {
        const difficultQuotasResposne =
          feasibilityData.response?.difficult_quotas || [];
        return quotas
          .map((quota) => {
            const found = difficultQuotasResposne.find(
              (difficultQuota) =>
                difficultQuota.buyer_quota_id === quota.buyer_quota_id
            );
            if (!found) {
              return null;
            }
            const {
              buyer_quota_id,
              criteria,
              required_count,
              quota_title,
              number,
              percentage,
              minimum,
              maximum,
              group_key,
            } = quota;
            const { quota_type } = found;
            return <DifficultQuota>{
              buyer_quota_id,
              criteria,
              quota_type,
              quota_title,
              required_count,
              number,
              percentage,
              minimum,
              maximum,
              group_key,
            };
          })
          .filter((quota): quota is DifficultQuota => !!quota);
      })
    );
  }

  public shouldShowUnicornQuotaHeader() {
    return !!this._nestedQuotas.length && this.unicornQuotas.length > 0;
  }

  public shouldShowUnicornDifficultQuotaHeader(): boolean {
    return !!(
      this.surveyDetails?.ps_survey_status === SURVEY_STATUS.DRAFT &&
      this.difficultQuotas.length > 0
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
